import { useEffect, useState, useSyncExternalStore } from "react";
import {
    ACCESS_RENEWAL_TICK_MS,
    accessLeaseExpired,
    accessRenewalDue,
    accessTicketIsCurrent,
    captureAccessTicket,
    resourceAccessEpoch,
    resourceAccessState,
    subscribeResourceAccess,
    suspendResourceAccess,
} from "./resourceAccess";
import { refreshResourceAdmission } from "./admissionClient";

type Admission = { scope: string; epoch: number; active: boolean; error: string | null };

/** One owner for initial admission, renewal, reconnect, expiry and recovery.
 * Cache restoration waits for this decision; a failed first request remains
 * recoverable by the same renewal path as an already mounted project.
 */
export function useResourceAdmission(scope: string, enabled: boolean) {
    const epoch = useSyncExternalStore(subscribeResourceAccess, resourceAccessEpoch);
    const [state, setState] = useState<Admission>();
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        if (!enabled) return;
        let stopped = false;
        let running = false;
        const publish = (active: boolean, error: string | null) => {
            if (!stopped) setState({ scope, epoch: resourceAccessEpoch(), active, error });
        };
        const refresh = async (force = true) => {
            if (stopped || running) return;
            running = true;
            const ticket = captureAccessTicket();
            try {
                const value = await refreshResourceAdmission(force);
                if (stopped) return;
                // A successful refresh may advance the policy epoch. A failed
                // request from a different scope cannot publish into this owner.
                if (!value && !accessTicketIsCurrent(ticket)) return;
                const allowed = Boolean(resourceAccessState()?.active) && !accessLeaseExpired();
                publish(
                    allowed,
                    allowed
                        ? null
                        : resourceAccessState()?.active === false
                          ? "Project access is inactive"
                          : navigator.onLine
                            ? "Project access could not be verified"
                            : "Connect to verify project access",
                );
            } finally {
                running = false;
            }
        };
        const expire = () => {
            if (accessLeaseExpired()) suspendResourceAccess();
        };
        const foreground = () => {
            expire();
            if (document.visibilityState !== "hidden") void refresh();
        };
        void refresh(attempt > 0);
        const renewal = window.setInterval(() => {
            if (accessRenewalDue()) void refresh();
        }, ACCESS_RENEWAL_TICK_MS);
        const expiry = window.setInterval(expire, 1000);
        window.addEventListener("online", foreground);
        window.addEventListener("offline", foreground);
        document.addEventListener("visibilitychange", foreground);
        return () => {
            stopped = true;
            clearInterval(renewal);
            clearInterval(expiry);
            window.removeEventListener("online", foreground);
            window.removeEventListener("offline", foreground);
            document.removeEventListener("visibilitychange", foreground);
        };
    }, [scope, enabled, epoch, attempt]);
    const current = state?.scope === scope && state.epoch === epoch ? state : undefined;
    return {
        ready: !enabled || current?.active === true,
        suspended:
            enabled &&
            resourceAccessState()?.active === true &&
            resourceAccessState()?.admitted === false,
        error: enabled ? current?.error : null,
        retry: () => setAttempt((value) => value + 1),
    };
}
